import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Hash } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageActions } from "@/app/pageActions/PageActionsContext";
import { isApiClientError } from "@/shared/api/errors";
import { useAuth } from "@/shared/auth/AuthContext";
import { hasPermission, PermissionCodes } from "@/shared/auth/permissions";
import {
  Banner,
  DataTable,
  DetailField,
  DetailFieldGrid,
  EntityFormDialog,
  EntityPeekModal,
  FormField,
  Input,
  Select,
  useToast,
  type DataTableRowAction,
} from "@/shared/ui";
import { entityFormModalSizeFor } from "@/shared/ui/core/entityFormModalPolicy";
import {
  ListPageTemplate,
  SearchStatusFilterBar,
} from "@/shared/ui/patterns/ListPageTemplate";
import {
  resetNumberingRule,
  updateNumberingRule,
  type NumberingRule,
  type NumberingRuleInput,
} from "@/modules/sys/api/sysApi";
import { useNumberingListQuery } from "@/modules/sys/queries/useSysQueries";

/** Radix Select rejects `""`. Map the empty separator to this option value. */
const SEPARATOR_NONE = "none";

const numberingQueryKey = ["sys", "numbering"] as const;

function documentTypeLabel(documentType: string): string {
  switch (documentType) {
    case "CUSTOMER":
      return "Khách hàng";
    case "SUPPLIER":
      return "Nhà cung cấp";
    case "SO":
      return "Đơn bán";
    case "PO":
      return "Đơn mua";
    case "PR":
      return "Đề nghị mua";
    case "DM":
      return "Nhu cầu";
    case "MO":
      return "Lệnh sản xuất";
    case "TTKT":
      return "Phiếu thông tin kỹ thuật";
    case "SHP":
      return "Phiếu giao";
    case "INSP":
      return "Phiếu kiểm";
    case "NCR":
      return "Biên bản sự cố";
    case "REL":
      return "Thẩm định xuất xưởng";
    case "DEV":
      return "Sai lệch";
    case "GR":
      return "Phiếu nhập";
    case "GI":
      return "Phiếu xuất";
    case "TRANSFER":
      return "Chuyển vị trí";
    case "ADJUST":
      return "Kiểm kê";
    case "DISPOSAL":
      return "Tiêu hủy";
    case "REVERSAL":
      return "Phiếu đảo";
    case "REC":
      return "Phiếu thu";
    case "PAY":
      return "Phiếu chi";
    default:
      return documentType;
  }
}

const DATE_FORMATS = [
  { value: "None", label: "Không gắn ngày" },
  { value: "ddMMyy", label: "ddMMyy · 071026" },
  { value: "ddMMyyyy", label: "ddMMyyyy · 07102026" },
  { value: "yyMMdd", label: "yyMMdd · 261007" },
  { value: "yyyyMMdd", label: "yyyyMMdd · 20261007" },
  { value: "yyyyMM", label: "yyyyMM · 202610" },
  { value: "yyyy", label: "yyyy · 2026" },
] as const;

const RESET_POLICIES = [
  { value: "Never", label: "Không reset" },
  { value: "Daily", label: "Mỗi ngày" },
  { value: "Monthly", label: "Mỗi tháng" },
  { value: "Yearly", label: "Mỗi năm" },
] as const;

const RESET_FILTER_OPTIONS = [
  { value: "all", label: "Tất cả" },
  ...RESET_POLICIES.map((item) => ({ value: item.value, label: item.label })),
];

type DateFormat = (typeof DATE_FORMATS)[number]["value"];
type ResetPolicy = (typeof RESET_POLICIES)[number]["value"];

const DAY_DATE_FORMATS = new Set<string>([
  "ddMMyy",
  "ddMMyyyy",
  "yyMMdd",
  "yyyyMMdd",
]);

function allowsReset(dateFormat: string, resetPolicy: string): boolean {
  if (resetPolicy === "Never") return true;
  if (dateFormat === "None") return false;
  if (resetPolicy === "Daily") return DAY_DATE_FORMATS.has(dateFormat);
  if (resetPolicy === "Monthly") return dateFormat !== "yyyy";
  if (resetPolicy === "Yearly") return true;
  return false;
}

function dateFormatLabel(dateFormat: string): string {
  return (
    DATE_FORMATS.find((item) => item.value === dateFormat)?.label ?? dateFormat
  );
}

function resetPolicyLabel(resetPolicy: string): string {
  return (
    RESET_POLICIES.find((item) => item.value === resetPolicy)?.label ??
    resetPolicy
  );
}

function separatorLabel(separator: string): string {
  return separator === "" ? "(không)" : separator;
}

function separatorSelectValue(separator: string): string {
  return separator === "-" || separator === "_" ? separator : SEPARATOR_NONE;
}

function separatorFromSelect(value: string): string {
  return value === "-" || value === "_" ? value : "";
}

function asDateFormat(value: string): DateFormat {
  return DATE_FORMATS.some((item) => item.value === value)
    ? (value as DateFormat)
    : "None";
}

function asResetPolicy(value: string, dateFormat: DateFormat): ResetPolicy {
  if (
    RESET_POLICIES.some((item) => item.value === value) &&
    allowsReset(dateFormat, value)
  ) {
    return value as ResetPolicy;
  }
  return "Never";
}

function apiErrorMessage(error: unknown, fallback: string): string {
  return isApiClientError(error) ? error.message : fallback;
}

function parsePadLength(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1 || value > 8) return null;
  return value;
}

function parseNextValue(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1) return null;
  return value;
}

/** `SCR-SYS-NUMBERING` — quy tắc đánh số chứng từ. */
export function NumberingAdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasPermission(
    user?.permissions ?? [],
    PermissionCodes.SysNumberingManage,
  );
  const list = useNumberingListQuery();
  const [viewing, setViewing] = useState<NumberingRule | null>(null);
  const [editing, setEditing] = useState<NumberingRule | null>(null);
  const [resetting, setResetting] = useState<NumberingRule | null>(null);

  usePageActions(
    useMemo(
      () => [
        {
          id: "settings-hub",
          label: "Thiết lập hệ thống",
          isQuickLink: true,
          onClick: () => navigate("/ops/sys/settings"),
        },
      ],
      [navigate],
    ),
  );

  return (
    <>
      <ListPageTemplate
        loading={list.isLoading}
        errorTitle={list.isError ? "Không tải được quy tắc đánh số" : undefined}
        errorDescription={
          list.isError && isApiClientError(list.error)
            ? list.error.message
            : undefined
        }
        onRetry={() => void list.refetch()}
        filtersOpen={list.filtersOpen}
        filterActiveCount={list.activeFilterCount}
        filter={
          <SearchStatusFilterBar
            open={list.filtersOpen}
            q={list.q}
            onQChange={list.setQ}
            searchPlaceholder="Tìm theo thực thể / tiền tố / mẫu xem trước"
            status={list.status}
            onStatusChange={list.setStatus}
            statusOptions={RESET_FILTER_OPTIONS}
            statusLabel="Chu kỳ reset"
            onClearAll={list.clearFilters}
          />
        }
        pagination={{
          page: list.page,
          pageSize: list.pageSize,
          total: list.total,
          loading: list.isFetching,
          onPageChange: list.setPage,
          onPageSizeChange: list.setPageSize,
        }}
      >
        <DataTable<NumberingRule>
          getRowId={(rule) => rule.documentType}
          rows={list.items}
          rowNumberStart={list.rowNumberStart}
          pageLoading={list.isFetching && !list.isLoading}
          loadingText="Đang tải trang..."
          emptyKeepHeader
          emptyTitle={
            list.q || (list.status && list.status !== "all")
              ? "Không tìm thấy quy tắc phù hợp"
              : "Chưa có quy tắc đánh số"
          }
          emptyDescription={
            list.q || (list.status && list.status !== "all")
              ? "Thử đổi từ khóa hoặc xóa bộ lọc."
              : undefined
          }
          emptyIcon={<Hash className="size-6" aria-hidden />}
          minWidthClassName="min-w-[960px]"
          onRowClick={setViewing}
          getRowActions={(rule): DataTableRowAction[] =>
            canManage
              ? [
                  {
                    id: "edit",
                    label: "Sửa",
                    onClick: () => setEditing(rule),
                  },
                  {
                    id: "reset",
                    label: "Đặt lại số",
                    onClick: () => setResetting(rule),
                  },
                ]
              : []
          }
          columns={[
            {
              id: "type",
              header: "Thực thể",
              cell: (rule) => documentTypeLabel(rule.documentType),
            },
            {
              id: "prefix",
              header: "Tiếp đầu ngữ",
              mono: true,
              cell: (rule) => rule.prefix,
            },
            {
              id: "date",
              header: "Ngày",
              cell: (rule) => dateFormatLabel(rule.dateFormat),
            },
            {
              id: "separator",
              header: "Dấu ngăn",
              align: "center",
              width: "1%",
              cell: (rule) => separatorLabel(rule.separator),
            },
            {
              id: "pad",
              header: "Số chữ số",
              dataType: "number",
              width: "1%",
              cell: (rule) => rule.padLength,
            },
            {
              id: "reset",
              header: "Reset",
              cell: (rule) => resetPolicyLabel(rule.resetPolicy),
            },
            {
              id: "next",
              header: "Số tiếp theo",
              dataType: "number",
              width: "1%",
              cell: (rule) => rule.nextValue,
            },
            {
              id: "preview",
              header: "Xem trước",
              mono: true,
              cell: (rule) => rule.preview,
            },
          ]}
        />
      </ListPageTemplate>

      <EntityPeekModal
        open={viewing != null}
        onOpenChange={(open) => {
          if (!open) setViewing(null);
        }}
        title={viewing ? documentTypeLabel(viewing.documentType) : "Đánh số"}
        description={viewing?.preview}
        icon={<Hash className="size-4" aria-hidden />}
        iconTone="info"
        headerTone="info"
        size="sm"
        height="default"
        footerSize="sm"
      >
        {viewing ? (
          <DetailFieldGrid columns={2}>
            <DetailField label="Thực thể">
              {documentTypeLabel(viewing.documentType)}
            </DetailField>
            <DetailField label="Tiếp đầu ngữ" mono>
              {viewing.prefix}
            </DetailField>
            <DetailField label="Ngày">
              {dateFormatLabel(viewing.dateFormat)}
            </DetailField>
            <DetailField label="Dấu ngăn">
              {separatorLabel(viewing.separator)}
            </DetailField>
            <DetailField label="Số chữ số">{viewing.padLength}</DetailField>
            <DetailField label="Reset">
              {resetPolicyLabel(viewing.resetPolicy)}
            </DetailField>
            <DetailField label="Số tiếp theo">{viewing.nextValue}</DetailField>
            <DetailField label="Xem trước" mono>
              {viewing.preview}
            </DetailField>
          </DetailFieldGrid>
        ) : null}
      </EntityPeekModal>

      {editing ? (
        <NumberingEditDialog
          rule={editing}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}
      {resetting ? (
        <NumberingResetDialog
          rule={resetting}
          onOpenChange={(open) => {
            if (!open) setResetting(null);
          }}
        />
      ) : null}
    </>
  );
}

function NumberingEditDialog({
  rule,
  onOpenChange,
}: {
  rule: NumberingRule;
  onOpenChange: (open: boolean) => void;
}) {
  const { http } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const initialDateFormat = asDateFormat(rule.dateFormat);
  const [prefix, setPrefix] = useState(rule.prefix);
  const [dateFormat, setDateFormat] = useState<DateFormat>(initialDateFormat);
  const [separator, setSeparator] = useState(
    separatorSelectValue(rule.separator),
  );
  const [padLength, setPadLength] = useState(String(rule.padLength));
  const [resetPolicy, setResetPolicy] = useState<ResetPolicy>(
    asResetPolicy(rule.resetPolicy, initialDateFormat),
  );
  const [padError, setPadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const resolvedSeparator = separatorFromSelect(separator);
  const resolvedReset = asResetPolicy(resetPolicy, dateFormat);
  const parsedPad = parsePadLength(padLength);
  const dirty =
    prefix !== rule.prefix ||
    dateFormat !== asDateFormat(rule.dateFormat) ||
    resolvedSeparator !==
      (rule.separator === "-" || rule.separator === "_"
        ? rule.separator
        : "") ||
    parsedPad !== rule.padLength ||
    resolvedReset !==
      asResetPolicy(rule.resetPolicy, asDateFormat(rule.dateFormat));

  const saveMut = useMutation({
    mutationFn: (body: NumberingRuleInput) =>
      updateNumberingRule(http, rule.documentType, body),
    onSuccess: async () => {
      toast({
        tone: "success",
        title: "Thao tác thành công",
        message: "Đã lưu quy tắc đánh số.",
      });
      onOpenChange(false);
      await qc.invalidateQueries({ queryKey: numberingQueryKey });
    },
    onError: (error) => {
      setFormError(apiErrorMessage(error, "Không lưu được quy tắc đánh số"));
    },
  });

  const save = () => {
    setFormError(null);
    if (parsedPad === null) {
      setPadError("Từ 1 đến 8");
      return;
    }
    setPadError(null);
    saveMut.mutate({
      prefix: prefix.trim().toUpperCase(),
      dateFormat,
      separator: resolvedSeparator,
      padLength: parsedPad,
      resetPolicy: resolvedReset,
    });
  };

  return (
    <EntityFormDialog
      open
      onOpenChange={onOpenChange}
      mode="edit"
      title={`Sửa ${documentTypeLabel(rule.documentType)}`}
      size={entityFormModalSizeFor("compact")}
      dirty={dirty}
      saving={saveMut.isPending}
      loadingLabel="Đang lưu..."
      primaryLabel="Lưu"
      cancelLabel="Hủy"
      onPrimary={save}
    >
      <div className="space-y-3 py-1">
        {formError ? <Banner tone="danger" title={formError} /> : null}
        <FormField label="Tiếp đầu ngữ" required>
          <Input
            value={prefix}
            autoComplete="off"
            className="font-mono uppercase"
            onChange={(e) => setPrefix(e.target.value.toUpperCase())}
          />
        </FormField>
        <Select
          label="Ngày"
          value={dateFormat}
          options={DATE_FORMATS.map((item) => ({
            value: item.value,
            label: item.label,
          }))}
          onValueChange={(value) => {
            const next = asDateFormat(value);
            setDateFormat(next);
            setResetPolicy((current) => asResetPolicy(current, next));
          }}
        />
        <Select
          label="Dấu ngăn"
          value={separator}
          options={[
            { value: SEPARATOR_NONE, label: "không" },
            { value: "-", label: "-" },
            { value: "_", label: "_" },
          ]}
          onValueChange={setSeparator}
        />
        <FormField label="Số chữ số" required error={padError ?? undefined}>
          <Input
            inputMode="numeric"
            value={padLength}
            onChange={(e) => {
              setPadLength(e.target.value);
              setPadError(null);
            }}
          />
        </FormField>
        <Select
          label="Reset"
          value={resolvedReset}
          options={RESET_POLICIES.map((item) => ({
            value: item.value,
            label: item.label,
            disabled: !allowsReset(dateFormat, item.value),
          }))}
          onValueChange={(value) => {
            setResetPolicy(asResetPolicy(value, dateFormat));
          }}
        />
      </div>
    </EntityFormDialog>
  );
}

function NumberingResetDialog({
  rule,
  onOpenChange,
}: {
  rule: NumberingRule;
  onOpenChange: (open: boolean) => void;
}) {
  const { http } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [nextValue, setNextValue] = useState(String(rule.nextValue));
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const parsed = parseNextValue(nextValue);

  const resetMut = useMutation({
    mutationFn: (value: number) =>
      resetNumberingRule(http, rule.documentType, value),
    onSuccess: async () => {
      toast({
        tone: "success",
        title: "Thao tác thành công",
        message: "Đã đặt lại số tiếp theo.",
      });
      onOpenChange(false);
      await qc.invalidateQueries({ queryKey: numberingQueryKey });
    },
    onError: (error) => {
      setFormError(apiErrorMessage(error, "Không đặt lại được số tiếp theo"));
    },
  });

  const save = () => {
    setFormError(null);
    if (parsed === null) {
      setFieldError("Từ 1");
      return;
    }
    setFieldError(null);
    resetMut.mutate(parsed);
  };

  return (
    <EntityFormDialog
      open
      onOpenChange={onOpenChange}
      mode="edit"
      title={`Đặt lại số · ${documentTypeLabel(rule.documentType)}`}
      size={entityFormModalSizeFor("compact")}
      dirty={nextValue.trim() !== String(rule.nextValue)}
      saving={resetMut.isPending}
      loadingLabel="Đang lưu..."
      primaryLabel="Đặt lại số"
      cancelLabel="Hủy"
      onPrimary={save}
    >
      <div className="space-y-3 py-1">
        {formError ? <Banner tone="danger" title={formError} /> : null}
        <FormField
          label="Số tiếp theo"
          required
          error={fieldError ?? undefined}
        >
          <Input
            inputMode="numeric"
            value={nextValue}
            onChange={(e) => {
              setNextValue(e.target.value);
              setFieldError(null);
            }}
          />
        </FormField>
      </div>
    </EntityFormDialog>
  );
}
